import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { Check, Loader2, Pencil, RotateCcw, Trash2, Undo2, X } from 'lucide-react';
import { getWebAdEdits, saveWebAdEdit } from '@generated/server/webAds';
import { getStaffPass } from '@generated/hooks/staffPass';

type Edit = { html: string | null; hidden: boolean };
type Ctx = { edits: Record<string, Edit>; save: (adId: string, patch: { html?: string | null; hidden?: boolean }) => Promise<void> };
const AdEditsCtx = createContext<Ctx>({ edits: {}, save: async () => undefined });

/** Loads staff edits/deletions for every ad on the Web Ads page. */
export function AdEditsProvider({ children }: { children: ReactNode }) {
  const [edits, setEdits] = useState<Record<string, Edit>>({});
  useEffect(() => {
    let on = true;
    getWebAdEdits({ data: { pass: getStaffPass() } })
      .then((rows) => { if (on) setEdits(Object.fromEntries((rows ?? []).map((r) => [r.adId, { html: r.html, hidden: r.hidden }]))); })
      .catch((e) => console.error('[webads] edits load failed', e));
    return () => { on = false; };
  }, []);
  const save = useCallback(async (adId: string, patch: { html?: string | null; hidden?: boolean }) => {
    let prev: Edit | undefined;
    setEdits((m) => { prev = m[adId]; return { ...m, [adId]: { html: patch.html !== undefined ? patch.html : m[adId]?.html ?? null, hidden: patch.hidden ?? m[adId]?.hidden ?? false } }; });
    try { await saveWebAdEdit({ data: { pass: getStaffPass(), adId, ...patch } }); }
    catch (e) { console.error('[webads] save failed', e); setEdits((m) => { const n = { ...m }; if (prev) n[adId] = prev; else delete n[adId]; return n; }); throw e; }
  }, []);
  return <AdEditsCtx.Provider value={{ edits, save }}>{children}</AdEditsCtx.Provider>;
}

const btn = 'inline-flex min-h-11 items-center gap-1.5 border border-foreground px-3 text-xs font-semibold disabled:opacity-50';

/**
 * Wraps one ready-made ad: shows saved wording if edited, lets staff click into the ad to retype
 * any words, and delete (hide) or restore it. `capture` receives the node used for image saving.
 */
export function AdFrame({ adId, label, className, style, capture, children }: { adId: string; label: string; className?: string; style?: React.CSSProperties; capture: (el: HTMLDivElement | null) => void; children: ReactNode }) {
  const { edits, save } = useContext(AdEditsCtx);
  const e = edits[adId];
  const node = useRef<HTMLDivElement | null>(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [armed, setArmed] = useState(false);
  const [ver, setVer] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const run = async (patch: { html?: string | null; hidden?: boolean }) => {
    setBusy(true); setError(null);
    try { await save(adId, patch); setVer((v) => v + 1); } catch { setError('That change could not be saved. Please try again.'); }
    finally { setBusy(false); }
  };
  const finish = async () => {
    const el = node.current; if (!el) return;
    const copy = el.cloneNode(true) as HTMLElement;
    // Inlined photos are huge; store their original web address instead.
    copy.querySelectorAll('img').forEach((img) => { if (img.getAttribute('src')?.startsWith('data:')) img.setAttribute('src', img.dataset.src ?? ''); });
    copy.querySelectorAll('[contenteditable]').forEach((n) => n.removeAttribute('contenteditable'));
    await run({ html: copy.innerHTML });
    setVer((v) => v + 1);
    setEditing(false);
  };

  if (e?.hidden) return (
    <div className="flex flex-wrap items-center justify-between gap-2 border border-dashed border-border px-4 py-3 text-sm text-muted-foreground">
      <span><strong className="text-foreground">{label}</strong> was deleted.</span>
      <button type="button" className={btn} disabled={busy} onClick={() => void run({ hidden: false })}><Undo2 className="h-4 w-4" />Restore</button>
    </div>
  );

  return (
    <div className="min-w-0">
      <div className={`max-w-full overflow-x-auto border bg-muted p-4 ${editing ? 'border-primary ring-2 ring-primary/40' : 'border-border'}`}>
        <div key={ver} ref={(el) => { node.current = el; capture(el); }} className={className} style={style}
          contentEditable={editing} suppressContentEditableWarning spellCheck={editing}
          {...(e?.html ? { dangerouslySetInnerHTML: { __html: e.html } } : {})}>
          {e?.html ? undefined : children}
        </div>
      </div>
      {editing && <p className="mt-2 text-xs text-muted-foreground">Click any words in the ad above and type to change them. Press Done to save.</p>}
      {error && <p role="alert" className="mt-2 text-sm text-destructive">{error}</p>}
      <div className="mt-2 flex flex-wrap gap-2">
        {editing ? <>
          <button type="button" className={`${btn} bg-foreground text-background`} disabled={busy} onClick={() => void finish()}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}Done</button>
          <button type="button" className={btn} disabled={busy} onClick={() => { setEditing(false); setVer((v) => v + 1); }}><X className="h-4 w-4" />Cancel</button>
        </> : <>
          <button type="button" className={btn} onClick={() => setEditing(true)}><Pencil className="h-4 w-4" />Edit wording</button>
          {e?.html && <button type="button" className={btn} disabled={busy} onClick={() => void run({ html: null })}><RotateCcw className="h-4 w-4" />Back to original</button>}
          <button type="button" className={`${btn} ${armed ? 'border-destructive bg-destructive text-primary-foreground' : 'border-destructive text-destructive'}`} disabled={busy}
            onClick={() => { if (armed) { setArmed(false); void run({ hidden: true }); } else { setArmed(true); setTimeout(() => setArmed(false), 4000); } }}>
            <Trash2 className="h-4 w-4" />{armed ? 'Press again to delete' : 'Delete'}</button>
        </>}
      </div>
    </div>
  );
}
